/**
 * Transactions page - main transaction management view
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTransactions, usePrefetchCategories } from '@/hooks';
import {
  TransactionList,
  TransactionFilters,
  TransactionDetail,
  TransactionSearch,
  BulkActions,
} from '@/components/transactions';
import { Card, Button } from '@/components/ui';
import type { TransactionFilters as TFilters } from '@/types';
import type { TransactionWithSplits } from '@/services/transactions';
import { cn } from '@/utils/cn';
import { ApiClientError } from '@/services/api';
import { getUserFriendlyMessage } from '@/utils/errorMessages';

/**
 * Build initial filters from URL search params
 */
function getFiltersFromSearchParams(searchParams: URLSearchParams): TFilters {
  const filters: TFilters = {};

  const accountId = searchParams.get('accountId');
  if (accountId) filters.accountId = accountId;

  const categoryId = searchParams.get('categoryId');
  if (categoryId) filters.categoryId = categoryId;

  const startDate = searchParams.get('startDate');
  if (startDate) filters.startDate = startDate;

  const endDate = searchParams.get('endDate');
  if (endDate) filters.endDate = endDate;

  const type = searchParams.get('type');
  if (type === 'income' || type === 'expense' || type === 'transfer') {
    filters.type = type;
  }

  const minAmount = searchParams.get('minAmount');
  if (minAmount) filters.minAmount = parseFloat(minAmount);

  const maxAmount = searchParams.get('maxAmount');
  if (maxAmount) filters.maxAmount = parseFloat(maxAmount);

  const search = searchParams.get('search');
  if (search) filters.search = search;

  // WP-FLOW-4: Support filtering by amount sign (from flow drill-down)
  const amountSign = searchParams.get('amountSign');
  if (amountSign === 'positive' || amountSign === 'negative') {
    filters.amountSign = amountSign;
  }

  return filters;
}

/**
 * Update URL search params from filters
 */
function updateSearchParams(
  filters: TFilters,
  searchQuery: string,
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void
): void {
  const params = new URLSearchParams();

  if (filters.accountId) params.set('accountId', filters.accountId);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.type) params.set('type', filters.type);
  if (filters.minAmount !== undefined) params.set('minAmount', String(filters.minAmount));
  if (filters.maxAmount !== undefined) params.set('maxAmount', String(filters.maxAmount));
  if (searchQuery) params.set('search', searchQuery);
  // WP-FLOW-4: Persist amount sign filter in URL
  if (filters.amountSign) params.set('amountSign', filters.amountSign);

  // Use replace to avoid cluttering browser history with every filter change
  setSearchParams(params, { replace: true });
}

/**
 * Main transactions page component
 */
export default function TransactionsPage() {
  // URL search params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL search params
  const initialFilters = useMemo(
    () => getFiltersFromSearchParams(searchParams),
    // Only compute on mount - we manage state internally after that
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialSearch = searchParams.get('search') || '';

  // Filter state
  const [filters, setFilters] = useState<TFilters>(initialFilters);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkMode, setShowBulkMode] = useState(false);

  // Detail modal state
  const [detailTransaction, setDetailTransaction] = useState<TransactionWithSplits | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Sync URL when filters change
  useEffect(() => {
    updateSearchParams(filters, searchQuery, setSearchParams);
  }, [filters, searchQuery, setSearchParams]);

  // Prefetch categories for better UX
  usePrefetchCategories();

  // Combine search with filters
  const activeFilters = useMemo(
    () => ({
      ...filters,
      ...(searchQuery && { search: searchQuery }),
    }),
    [filters, searchQuery]
  );

  // Fetch transactions with infinite query
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useTransactions(activeFilters);

  // Flatten paginated results - useInfiniteQuery returns InfiniteData with pages array
  const transactions = useMemo(() => {
    if (!data?.pages) {
      if (import.meta.env.DEV) {
        console.debug('[TransactionsPage] No pages in data:', { hasData: !!data, data });
      }
      return [];
    }
    const flattenedTransactions = data.pages.flatMap((page) => page.transactions ?? []);
    if (import.meta.env.DEV) {
      console.debug('[TransactionsPage] Flattened transactions:', {
        pagesCount: data.pages.length,
        transactionsCount: flattenedTransactions.length,
        firstPageTransactionsCount: data.pages[0]?.transactions?.length ?? 0,
        firstPagePagination: data.pages[0]?.pagination,
      });
    }
    return flattenedTransactions;
  }, [data]);

  // Get total count from first page
  const totalCount = useMemo(() => {
    return data?.pages?.[0]?.pagination?.totalItems ?? 0;
  }, [data]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: TFilters) => {
    setFilters(newFilters);
    setSelectedIds(new Set()); // Clear selection on filter change
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({});
    setSearchQuery('');
    setSelectedIds(new Set());
  }, []);

  // Handle transaction selection
  const handleSelectTransaction = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setShowBulkMode(false);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === transactions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(transactions.map((t) => t.id)));
    }
  }, [selectedIds.size, transactions]);

  // Handle transaction click
  const handleTransactionClick = useCallback((transaction: TransactionWithSplits) => {
    if (showBulkMode) {
      handleSelectTransaction(transaction.id);
    } else {
      setDetailTransaction(transaction);
      setIsDetailOpen(true);
    }
  }, [showBulkMode, handleSelectTransaction]);

  const handleDetailClose = useCallback(() => {
    setIsDetailOpen(false);
    setDetailTransaction(null);
  }, []);

  // Handle bulk action success
  const handleBulkSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Error state with user-friendly message
  if (isError) {
    // Extract meaningful error message
    let errorMessage = 'An unexpected error occurred while loading transactions.';

    // Use the shared mapper, not a private copy of the rules.
    //
    // This used to classify by substring-matching the message:
    //     error.message.includes('500') || error.message.includes('internal')
    // ApiClientError's message is the RFC 7807 `detail` (services/api.ts:50),
    // which for a 500 is "Internal server error" - capital I - so that branch
    // was case-sensitively dead and the user saw the raw server string instead
    // of the sentence written for them.
    //
    // getUserFriendlyMessage already maps ErrorCategory to user-facing text and
    // ApiClientError already carries .category, set from the status
    // (services/api.ts). DashboardPage uses it (DashboardPage.tsx:36-37); there
    // is no reason for this page to hold a second, divergent copy of the rules.
    // It also appends a truncated request id for support, which the hand-rolled
    // version dropped.
    if (error instanceof ApiClientError) {
      errorMessage = getUserFriendlyMessage(error.category, error.requestId);
    } else if (error instanceof Error) {
      // A transport failure never reaches ApiClientError - there is no response
      // to build one from - so it is still recognised by message here.
      if (error.message.includes('Network') || error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      } else {
        errorMessage = error.message;
      }
    }

    return (
      <div className="max-w-7xl mx-auto">
        <Card className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Unable to Load Transactions
          </h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            {errorMessage}
          </p>
          <div className="mt-6 flex justify-center space-x-3">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
            <p className="mt-1 text-sm text-gray-500">
              {isLoading ? (
                'Loading...'
              ) : (
                <>
                  {totalCount.toLocaleString()} transaction{totalCount !== 1 ? 's' : ''}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Bulk mode toggle */}
            <Button
              variant={showBulkMode ? 'primary' : 'outline'}
              size="sm"
              onClick={() => {
                setShowBulkMode(!showBulkMode);
                if (showBulkMode) {
                  setSelectedIds(new Set());
                }
              }}
              leftIcon={
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
              }
            >
              {showBulkMode ? 'Exit Bulk Mode' : 'Bulk Edit'}
            </Button>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <TransactionSearch
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by merchant, description..."
          className="w-full sm:max-w-md"
        />
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        onChange={handleFilterChange}
        onReset={handleResetFilters}
        className="mb-4"
      />

      {/* Select all (when in bulk mode) */}
      {showBulkMode && transactions.length > 0 && (
        <div className="mb-4 flex items-center px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <input
            type="checkbox"
            checked={selectedIds.size === transactions.length && transactions.length > 0}
            onChange={handleSelectAll}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            aria-label="Select all transactions"
          />
          <span className="ml-3 text-sm text-gray-700">
            {selectedIds.size === transactions.length
              ? 'Deselect all'
              : `Select all ${transactions.length} transactions`}
          </span>
        </div>
      )}

      {/* Transaction list */}
      <Card padding="none" className="overflow-hidden">
        <div className="h-[calc(100vh-350px)] min-h-[300px] md:min-h-[400px]">
          <TransactionList
            transactions={transactions}
            selectedIds={selectedIds}
            onSelectTransaction={handleSelectTransaction}
            onClickTransaction={handleTransactionClick}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            showCheckboxes={showBulkMode}
          />
        </div>
      </Card>

      {/* Transaction detail drawer */}
      <TransactionDetail
        transaction={detailTransaction}
        isOpen={isDetailOpen}
        onClose={handleDetailClose}
        onTransactionUpdated={() => refetch()}
      />

      {/* Bulk actions bar */}
      <BulkActions
        selectedCount={selectedIds.size}
        selectedIds={Array.from(selectedIds)}
        onClearSelection={handleClearSelection}
        onSuccess={handleBulkSuccess}
        className={cn(selectedIds.size > 0 ? 'translate-y-0' : 'translate-y-full')}
      />
    </div>
  );
}
